"use client";

import { Icons } from "@/components/icons";
import { DEVELOPER_CARDS } from "../_data/page-data";
import {
  AnimatedSection,
  StaggerGrid,
  FadeInChild,
  GlowCard,
} from "./motion";

/* ─── Code block renderers for each card type ─── */

function NpmCodeBlock() {
  return (
    <div className="rounded-lg bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-slate-300 overflow-x-auto">
      <span className="text-emerald-400">$</span>{" "}
      <span className="text-slate-100">npm install botbyte-ai</span>
      <br />
      <br />
      <span className="text-violet-400">import</span>
      {" { "}
      <span className="text-amber-300">BotByte</span>
      {" } "}
      <span className="text-violet-400">from</span>{" "}
      <span className="text-emerald-300">&quot;botbyte-ai&quot;</span>
    </div>
  );
}

function ApiCodeBlock() {
  return (
    <div className="rounded-lg bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-slate-300 overflow-x-auto">
      <span className="text-emerald-400">GET</span>{" "}
      <span className="text-slate-400">/api/v1/chatbots</span>
      <br />
      <span className="text-amber-400">POST</span>{" "}
      <span className="text-slate-400">/api/v1/chatbots/:id/messages</span>
      <br />
      <span className="text-cyan-400">GET</span>{" "}
      <span className="text-slate-400">/api/v1/chatbots/:id/analytics</span>
    </div>
  );
}

function WebhookEvents({ events }: { events: readonly string[] }) {
  return (
    <div className="space-y-1.5">
      {events.map((e) => (
        <div key={e} className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <code className="text-[11px] font-mono text-slate-600">{e}</code>
        </div>
      ))}
    </div>
  );
}

function MetricsGrid({ metrics }: { metrics: readonly { label: string; val: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {metrics.map((m) => (
        <div key={m.label} className="rounded-md bg-slate-50 p-2 text-center">
          <p className="text-sm font-bold text-slate-800">{m.val}</p>
          <p className="text-[10px] text-slate-400">{m.label}</p>
        </div>
      ))}
    </div>
  );
}

/* ─── Card body selector ─── */
function CardBody({ card }: { card: (typeof DEVELOPER_CARDS)[number] }) {
  switch (card.title) {
    case "npm Package":
      return (
        <>
          <NpmCodeBlock />
          {"npmUrl" in card && card.npmUrl && (
            <a
              href={card.npmUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-800 transition-colors"
            >
              View on npm <span aria-hidden>→</span>
            </a>
          )}
        </>
      );
    case "REST API":
      return <ApiCodeBlock />;
    case "Real-Time Webhooks":
      return "events" in card && card.events ? (
        <WebhookEvents events={card.events} />
      ) : null;
    case "Analytics Engine":
      return "metrics" in card && card.metrics ? (
        <MetricsGrid metrics={card.metrics} />
      ) : null;
    default:
      return null;
  }
}

export function DeveloperSection() {
  return (
    <AnimatedSection id="developer" className="container py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center space-y-4 text-center mb-12">
          <span className="inline-flex items-center rounded-full bg-amber-50 px-4 py-1 text-xs font-semibold text-amber-600 ring-1 ring-inset ring-amber-200">
            Developer-First
          </span>
          <h2 className="font-heading text-3xl leading-[1.1] sm:text-3xl md:text-6xl">
            Built for <span className="text-gradient">developers</span>
          </h2>
          <p className="max-w-[600px] leading-relaxed text-muted-foreground sm:text-lg">
            A complete REST API, scoped access keys, real-time webhooks, and an analytics engine —
            everything you need to go beyond the UI.
          </p>
        </div>

        <StaggerGrid className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {DEVELOPER_CARDS.map((card) => {
            const Ic = Icons[card.iconKey as keyof typeof Icons];
            return (
              <FadeInChild key={card.title}>
                <GlowCard className="h-full">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${card.glowGradient} opacity-0 group-hover:opacity-100 transition-opacity`}
                  />
                  <div className="relative p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${card.gradient} shadow-sm`}
                      >
                        <Ic className="h-5 w-5 text-white" />
                      </div>
                      {card.isNew && (
                        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-sky-500 to-cyan-600 px-2 py-0.5 text-[9px] font-bold text-white uppercase tracking-wider shadow-sm">
                          New
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{card.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{card.desc}</p>
                    <CardBody card={card} />
                  </div>
                </GlowCard>
              </FadeInChild>
            );
          })}
        </StaggerGrid>
      </div>
    </AnimatedSection>
  );
}
